import { useState, useRef } from 'react'
import { Button, ContentCard, Grid, MultiSelect, Select } from '@forwardflow/ui-v2'
import { ArrowLeftMdIcon, CloseIcon, SearchMagnifyingGlassIcon } from '@forwardflow/icons'
import { Loader } from '@mantine/core'
import { TLeadType } from '../interfaces'
import { useCampaignFilterContext } from '../hooks'
import { useLeadOptions } from '../hooks'
import { useDebouncedValue } from '@forwardflow/hooks/customHooks/useDebouncedValue'
import { FILTER_LABEL_MAP } from '../constants'
import { InquiredPropertyInput } from './InquiredPropertyInput'

export const FilterConfirmation = ({
   setStep,
   selectedFilterId,
}: {
   setStep: (step: number) => void
   selectedFilterId: TLeadType
}) => {
   const { configuration, setFirstFilterCriteria } = useCampaignFilterContext()

   const selectedValues =
      configuration?.campaign_filters?.eligible_criteria[0]?.value || []
   const currentField =
      configuration?.campaign_filters?.eligible_criteria[0]?.field
   const currentOperator =
      configuration?.campaign_filters?.eligible_criteria[0]?.operator || 'CONTAINS_ANY'
   const [selectedOptions, setSelectedOptions] = useState<string[]>(
      currentField === selectedFilterId ? selectedValues.map(String) : []
   )
   const [isExclude, setIsExclude] = useState(currentOperator === 'NOT_CONTAINS_ANY')
   const isInquiredProperty = selectedFilterId === 'inquired_property'

   const [searchValue, setSearchValue] = useState('')
   const [debouncedSearch] = useDebouncedValue(searchValue, 500)
   const { options, isLoading, loadMore, hasMore } = useLeadOptions(
      selectedFilterId,
      debouncedSearch
   )
   const dropdownRef = useRef<HTMLElement | null>(null)
   const isLoadingMore = useRef(false)

   const justSelected = useRef(false)

   const handleSearchChange = (value: string) => {
      if (value === '' && justSelected.current) {
         justSelected.current = false
         return
      }
      setSearchValue(value)
   }

   const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      if (hasMore && !isLoading && !isLoadingMore.current) {
         const { scrollHeight, clientHeight, scrollTop } = target
         if (scrollTop + clientHeight >= scrollHeight - 100) {
            isLoadingMore.current = true
            loadMore()
            setTimeout(() => {
               isLoadingMore.current = false
            }, 1000)
         }
      }
   }

   const handleChange = (values: string[]) => {
      justSelected.current = true
      setSelectedOptions(values)
   }

   const handleRemove = (value: string) => {
      setSelectedOptions((prev) => prev.filter((v) => v !== value))
   }

   const handleConfirmSelection = () => {
      setFirstFilterCriteria({
         field: selectedFilterId as TLeadType,
         value: selectedOptions,
         logic: 'AND',
         operator:
            isInquiredProperty || !isExclude
               ? 'CONTAINS_ANY'
               : 'NOT_CONTAINS_ANY',
      })
      setStep(4)
   }

   const filterLabel =
      FILTER_LABEL_MAP[selectedFilterId as keyof typeof FILTER_LABEL_MAP]

   return (
      <ContentCard className="space-y-4">
         <p className="text-white font-semibold text-lg">
            Which leads do you want Forward Flow to work on first?
         </p>
         <div className="flex items-center gap-3">
            <span className="text-sm text-white/70">
               {isInquiredProperty
                  ? `Add the ${filterLabel} you'd like to target.`
                  : `Select the ${filterLabel} you'd like to`}
            </span>
            {!isInquiredProperty && (
               <Select
                  data={[
                     { value: 'include', label: 'Include' },
                     { value: 'exclude', label: 'Exclude' },
                  ]}
                  value={isExclude ? 'exclude' : 'include'}
                  onChange={(value) => setIsExclude(value === 'exclude')}
                  w={120}
               />
            )}
         </div>
         {selectedFilterId === 'inquired_property' ? (
            <InquiredPropertyInput
               value={selectedOptions}
               onChange={handleChange}
               placeholder="Search property addresses"
            />
         ) : (
            <MultiSelect
               searchable
               placeholder={`Search and select ${filterLabel}`}
               leftSection={<SearchMagnifyingGlassIcon width={16} />}
               rightSection={isLoading ? <Loader size="xs" /> : undefined}
               data={options}
               value={selectedOptions}
               nothingFoundMessage={isLoading ? 'Loading...' : "No results found"}
               onChange={handleChange}
               searchValue={searchValue}
               onSearchChange={handleSearchChange}
               classNames={{ pill: '!hidden' }}
               onDropdownOpen={() => {
                  setTimeout(() => {
                     const dropdown = document.querySelector(
                        '.mantine-MultiSelect-dropdown .mantine-ScrollArea-viewport'
                     ) as HTMLElement
                     if (dropdown) {
                        dropdown.addEventListener('scroll', handleScroll)
                        dropdownRef.current = dropdown
                     }
                  }, 0)
               }}
               onDropdownClose={() => {
                  if (dropdownRef.current) {
                     dropdownRef.current.removeEventListener(
                        'scroll',
                        handleScroll
                     )
                     dropdownRef.current = null
                  }
               }}
            />
         )}
         {selectedFilterId !== 'inquired_property' && selectedOptions.length > 0 && (
            <div className="flex flex-wrap gap-2">
               {selectedOptions.map((value, index) => {
                  const label =
                     selectedFilterId === 'assigned_pond_id'
                        ? options.find((i) => i.value === value)?.label
                        : value
                  return (
                     <div
                        key={index}
                        className="flex items-center gap-2 rounded-md border border-[#A684FF]/30 bg-[#A684FF]/10 py-1.5 px-3"
                     >
                        <p className="text-white text-xs font-medium">
                           {label || value}
                        </p>
                        <CloseIcon
                           width={12}
                           height={12}
                           className="cursor-pointer shrink-0"
                           onClick={() => handleRemove(value)}
                        />
                     </div>
                  )
               })}
            </div>
         )}
         <Grid>
            <Grid.Col span={3}>
               <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => setStep(2)}
               >
                  <ArrowLeftMdIcon width={12} /> Back
               </Button>
            </Grid.Col>
            <Grid.Col span={9}>
               <Button
                  className="w-full"
                  disabled={selectedOptions.length === 0}
                  onClick={() => handleConfirmSelection()}
               >
                  Confirm Selection
               </Button>
            </Grid.Col>
         </Grid>
      </ContentCard>
   )
}
